// The source-paper engine's pure parts: the guided steps, the saved-row keys,
// the rail ticks, and the engine's own learner-facing lines (Leah's Paper 1
// spec §6, taken from the reference build). Pure and server-safe, like
// format.ts: the player and the slide registry both read it.
//
// Every line here is generic. Anything that names this practice's history
// comes from the block; question numbers and source letters come from the
// questions, because they change with the prescribed subject (spec §3).

import type { SourcePaperBlock, SourcePaperQuestion, SourcePaperRole, SourcePaperSourceBody } from '../types';
import { SOURCE_PAPER_FORMAT, forVariant, sourcePaperBody, sourcePaperVariantSource } from '../types';
import { SOURCE_PAPER_SKILLS } from './format';

// ---------------------------------------------------------------------------
// Steps
// ---------------------------------------------------------------------------

export type SourcePaperStepId = 'revise' | 'paper' | SourcePaperRole | 'answers';

export interface SourcePaperStep {
  id: SourcePaperStepId;
  /** Rail label, e.g. "Question 9 (a)". */
  title: string;
  /** The question, on the five question steps. */
  question?: SourcePaperQuestion;
}

/** The guided steps in rail order: Revise, the paper, one per question, your answers. */
export function sourcePaperSteps(p: SourcePaperBlock): SourcePaperStep[] {
  return [
    { id: 'revise', title: 'Revise' },
    { id: 'paper', title: 'The paper' },
    ...p.questions.map((q) => ({ id: q.role, title: `Question ${q.n}`, question: q })),
    { id: 'answers', title: 'Your answers' },
  ];
}

/** The two revision packs: this practice's content, and the shared skills. */
export type RevisePack = 'content' | 'skills';
export const REVISE_PACKS: readonly RevisePack[] = ['content', 'skills'];

export interface PackState {
  /** Finished once at least; the menu then offers "Revise again". */
  done: boolean;
  /** The topic the carousel is on. */
  topic: number;
  /** Per topic: the tile ids turned over ("p0", "t2"). */
  open: Record<number, string[]>;
}

export const emptyPack = (): PackState => ({ done: false, topic: 0, open: {} });

/** What the rail ticks need. Question steps tick once an answer is written and checked, which arrives with the question screens. */
export interface RailState {
  packs: Record<RevisePack, PackState>;
  seenPaper: boolean;
}

/** The rail tick for a step (spec §7.2). The rail itself is never locked. */
export function stepDone(step: SourcePaperStep, s: RailState): boolean {
  if (step.id === 'revise') return s.packs.content.done && s.packs.skills.done;
  if (step.id === 'paper') return s.seenPaper;
  return false;
}

// ---------------------------------------------------------------------------
// Saved rows
//
// Every row here is an upsert (one row per key, overwritten), because each
// holds the current state of something the learner changes freely: where they
// are, which version of the variant source they chose, which tiles they have
// turned over. The rail is never locked (spec §7.2), so unlike the essay
// workshop there is no "first unfinished step" to land on, and the position is
// stored.
// ---------------------------------------------------------------------------

export const SOURCE_PAPER_KEYS = {
  position: 'sp:pos',
  variant: 'sp:variant',
  paper: 'sp:paper',
  revise: (pack: RevisePack) => `sp:revise:${pack}`,
} as const;

/** Every key a source-paper slide may write, for the slide registry. */
export function sourcePaperActivityKeys(p: SourcePaperBlock): string[] {
  return [
    SOURCE_PAPER_KEYS.position,
    ...(sourcePaperVariantSource(p) ? [SOURCE_PAPER_KEYS.variant] : []),
    SOURCE_PAPER_KEYS.paper,
    ...REVISE_PACKS.map((pack) => SOURCE_PAPER_KEYS.revise(pack)),
  ];
}

// ---------------------------------------------------------------------------
// Reading the block
// ---------------------------------------------------------------------------

/** The version the learner starts on: the default, or null when no source varies. */
export function initialVariant(p: SourcePaperBlock): string | null {
  const vs = sourcePaperVariantSource(p);
  if (!vs) return null;
  return p.defaultVariant && vs.variants[p.defaultVariant] ? p.defaultVariant : Object.keys(vs.variants)[0];
}

/** A question's text for the chosen version. */
export function questionText(q: SourcePaperQuestion, variant: string | null): string {
  return forVariant(q.text, variant) ?? '';
}

/** The question in a role. */
export const questionFor = (p: SourcePaperBlock, role: SourcePaperRole) => p.questions.find((q) => q.role === role);

/** A source's body for the chosen version. */
export function bodyFor(p: SourcePaperBlock, letter: string, variant: string | null): SourcePaperSourceBody | undefined {
  const s = p.sources[letter];
  return s ? sourcePaperBody(s, variant) : undefined;
}

/** The leading number of a printed question number: "9 (a)" → "9". */
export const questionNumber = (n: string) => n.match(/^\d+/)?.[0] ?? n;

/** Small numbers as words, for "three relevant points". */
const WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
export const numberWord = (n: number) => WORDS[n] ?? String(n);

/** "The Abyssinian crisis" → "the Abyssinian crisis", for use mid-sentence. */
export const lowerFirst = (s: string) => (s ? s[0].toLowerCase() + s.slice(1) : s);

/** One revision tile: a cue and its point, or a source type and its value and limitation. */
export interface RevisionTile {
  id: string;
  front: string;
  back?: string;
  values?: string;
  limitations?: string;
}

/** The tiles for one topic: its points, then any type-table rows. */
export function revisionTiles(card: { cues: string[]; points: string[]; types?: Array<{ type: string; values: string; limitations: string }> }): RevisionTile[] {
  return [
    ...card.cues.map((front, i) => ({ id: `p${i}`, front, back: card.points[i] })),
    ...(card.types ?? []).map((t, i) => ({ id: `t${i}`, front: t.type, values: t.values, limitations: t.limitations })),
  ];
}

/** The cards in a pack. */
export const packCards = (p: SourcePaperBlock, pack: RevisePack) => (pack === 'content' ? p.revise.cards : SOURCE_PAPER_SKILLS);

// ---------------------------------------------------------------------------
// The engine's own lines (spec §6.3, §6.4; the reference build's wording)
// ---------------------------------------------------------------------------

/** What each question asks of you, on the "how Paper 1 works" step. */
export function roleAsks(q: SourcePaperQuestion): string {
  const points = numberWord(q.marks);
  switch (q.role) {
    case 'extract':
      return `Find ${points} relevant points in Source ${q.sources[0]}. Use only what the source says. One mark for each point.`;
    case 'visual':
      return `Find ${points} points the visual source suggests. Look at the details and the caption. One mark for each point.`;
    case 'opvl':
      return 'Judge the source’s value and its limitations for the historian named in the question. Use its origin, purpose and content. You need value and limitations, and a reference to each of origin, purpose and content, for full marks.';
    case 'compare':
      return 'Find points where the two sources agree and points where they differ. Link the sources in each point instead of describing them one after the other.';
    case 'essay':
      return 'Answer the question with an argument. Use the sources as evidence and add your own knowledge. Reach a judgement.';
  }
}

/** The "how Paper 1 works" lines that name question numbers, built from this paper's numbering. */
export function paperLines(p: SourcePaperBlock) {
  const essay = questionFor(p, 'essay');
  const opvl = questionFor(p, 'opvl');
  const first = questionFor(p, 'extract');
  const compare = questionFor(p, 'compare');
  const en = essay?.n ?? '';
  return {
    lede: `Paper 1 lasts one hour and is marked out of ${SOURCE_PAPER_FORMAT.totalMarks}. You answer four questions on four sources. Each question asks you to do something different with the sources.`,
    readFirst: `Before you answer, read all four sources. For each one, note who made it, when, and why. Those details matter for every question, not only question ${opvl?.n ?? ''}.`,
    buildHead: `Build question ${en} as you go`,
    buildText: `Question ${en} comes last, when you are tired and short of time. By then you have read every source closely, so collect your evidence while you answer the other questions.`,
    moves: [
      { head: `Read question ${en} first`, text: `In reading time, read question ${en} before anything else. Work out what it is asking.` },
      { head: 'Draw the table', text: 'When writing starts, draw three columns: source, evidence that supports, evidence that counters. One row per source, plus own knowledge.' },
      { head: 'Thirty seconds per source', text: 'After each question, add one or two notes to that source’s row while it is fresh.' },
      { head: 'Write from the table', text: `Answer question ${en} in one sentence, then build your sections from the rows.` },
    ],
    saved: `In this practice your table is saved as you go. You will find it under questions ${questionNumber(first?.n ?? '')} to ${questionNumber(compare?.n ?? '')}, and in full at question ${en}.`,
  };
}

/** The suggested minutes for a question, by its role (spec §3). */
export const roleMinutes = (q: SourcePaperQuestion) => SOURCE_PAPER_FORMAT.roles[q.role].minutes;

/** The revise menu's lines. */
export function reviseLines(p: SourcePaperBlock) {
  return {
    lede: `Revise ${lowerFirst(p.revise.title)}, the techniques for each Paper 1 question, or both. Each topic is a set of cards. Click a card to reveal what is behind it. If you are ready, skip straight to the paper.`,
    content: { label: 'Content', title: p.revise.title, text: `${p.revise.cards.length} topics: ${p.revise.blurb}` },
    skills: { label: 'Skills', title: 'Paper 1 techniques', text: `${SOURCE_PAPER_SKILLS.length} topics on the skills for any Paper 1: timing, each type of question, origin, purpose, value and limitations, types of source, and the source table.` },
  };
}

/** The carousel heading for a pack: "Revise: the Abyssinian crisis". */
export const packHeading = (p: SourcePaperBlock, pack: RevisePack) => (pack === 'content' ? `Revise: ${lowerFirst(p.revise.title)}` : 'Revise: Paper 1 techniques');

/** The start screen's help line under the variant choice. */
export function variantHelp(p: SourcePaperBlock): string | null {
  const vs = sourcePaperVariantSource(p);
  if (!vs) return null;
  const uses = p.questions.filter((q) => q.sources.includes(vs.letter) && q.role !== 'essay').map((q) => `question ${q.n}`);
  const essay = questionFor(p, 'essay');
  const changes = uses.length ? `changes ${uses.join(' and ')} and gives` : 'gives';
  return `Source ${vs.letter} ${changes} you different evidence for question ${essay?.n ?? ''}. You can switch later from this screen. Your answers for each version are kept separately.`;
}
